import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Coins, RotateCw, X, Undo2 } from "lucide-react";
import { useApp } from "../context/AppContext";
import { gamesApi } from "../lib/api";
import { inr } from "../lib/format";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";
import { WinReveal } from "../components/WinReveal";
import { LastResults } from "../components/LastResults";
import { LuxeWheel, WheelSheen, PearlBall } from "../components/WheelLuxe";
import { playRouletteSpin, unlockAudio, playCoinSplash } from "../lib/rouletteSound";
import { TodaysWinners } from "../components/TodaysWinners";

// Single-Zero Roulette — 37 pockets (0, 1-36), real casino wheel order.
const WHEEL = [
  "0", "32", "15", "19", "4", "21", "2", "25", "17", "34", "6", "27", "13",
  "36", "11", "30", "8", "23", "10", "5", "24", "16", "33", "1", "20", "14",
  "31", "9", "22", "18", "29", "7", "28", "12", "35", "3", "26",
];
const RED = new Set([1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36]);
const colorOf = (label) => (label === "0" ? "green" : RED.has(Number(label)) ? "red" : "black");
const FILL = { red: "#dc2626", black: "#18181b", green: "#16a34a" };

const CX = 150, CY = 150, R = 142;
const P = WHEEL.length;
const SEG = 360 / P;


const ROW_TOP = [3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36];
const ROW_MID = [2, 5, 8, 11, 14, 17, 20, 23, 26, 29, 32, 35];
const ROW_BOT = [1, 4, 7, 10, 13, 16, 19, 22, 25, 28, 31, 34];

// bet-id prefix -> backend type
const TYPE_MAP = { s: "straight", sp: "split", st: "street", cr: "corner", d: "dozen", c: "column", col: "color", p: "parity", h: "half" };
const key = (arr) => [...arr].sort((a, b) => a - b).join("-");

const PRO_MAX_COVERAGE = 30;
const RED_SET = new Set([1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36]);
const rangeNums = (lo, hi) => Array.from({ length: hi - lo + 1 }, (_, i) => lo + i);
// Distinct wheel numbers (0-36) a single bet id covers — mirrors the backend guard.
const numbersOf = (id) => {
  const i = id.indexOf(":");
  const type = TYPE_MAP[id.slice(0, i)];
  const v = id.slice(i + 1);
  if (type === "straight") return [parseInt(v, 10)];
  if (type === "split" || type === "street" || type === "corner") return v.split("-").map(Number);
  if (type === "dozen") { const m = { "1-12": [1, 12], "13-24": [13, 24], "25-36": [25, 36] }[v]; return rangeNums(m[0], m[1]); }
  if (type === "column") { const r = { "1": 1, "2": 2, "3": 0 }[v]; return rangeNums(1, 36).filter((n) => n % 3 === r); }
  if (type === "color") return v === "red" ? [...RED_SET] : rangeNums(1, 36).filter((n) => !RED_SET.has(n));
  if (type === "parity") return rangeNums(1, 36).filter((n) => (n % 2 === 0) === (v === "even"));
  if (type === "half") { const m = { "1-18": [1, 18], "19-36": [19, 36] }[v]; return rangeNums(m[0], m[1]); }
  return [];
};
const coveredCount = (ids) => {
  const s = new Set();
  ids.forEach((id) => numbersOf(id).forEach((n) => s.add(n)));
  return s.size;
};

// fine grid: zero | (num, gap)*11 | num | 2:1
const FINE_COLS = "30px repeat(11, 40px 3px) 40px 34px";
const FINE_ROWS = "6px 40px 3px 40px 3px 40px";
const BOARD_W = 577;

// build inside-bet hitboxes (splits / streets / corners) with grid coords
const INSIDE = (() => {
  const out = [];
  for (let j = 1; j <= 12; j++) {
    const top = 3 * j, mid = 3 * j - 1, bot = 3 * j - 2;
    const cN = 2 * j;                                   // number column track
    out.push({ id: `st:${key([bot, mid, top])}`, gc: cN, gr: 1, shape: "line" });          // street (top edge)
    out.push({ id: `sp:${key([mid, top])}`, gc: cN, gr: 3, shape: "line" });               // vertical split
    out.push({ id: `sp:${key([bot, mid])}`, gc: cN, gr: 5, shape: "line" });
    if (j < 12) {
      const gc = 2 * j + 1;                             // gap track between columns
      out.push({ id: `sp:${key([top, top + 3])}`, gc, gr: 2, shape: "line" });             // horizontal splits
      out.push({ id: `sp:${key([mid, mid + 3])}`, gc, gr: 4, shape: "line" });
      out.push({ id: `sp:${key([bot, bot + 3])}`, gc, gr: 6, shape: "line" });
      out.push({ id: `cr:${key([mid, top, mid + 3, top + 3])}`, gc, gr: 3, shape: "corner" }); // corners
      out.push({ id: `cr:${key([bot, mid, bot + 3, mid + 3])}`, gc, gr: 5, shape: "corner" });
    }
  }
  return out;
})();

export default function ProRoulette() {
  const { isAuthed, balance, refresh } = useApp();
  const { toast } = useToast();
  const [chip, setChip] = useState(100);
  const [bets, setBets] = useState({});
  const [history, setHistory] = useState([]);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [reveal, setReveal] = useState(null);
  const [wheelRot, setWheelRot] = useState(0);
  const [ballRot, setBallRot] = useState(0);
  const [lastBet, setLastBet] = useState(null);
  const streakRef = useRef(0);
  const [recent, setRecent] = useState(() =>
    Array.from({ length: 50 }, () => {
      const label = WHEEL[Math.floor(Math.random() * WHEEL.length)];
      return { label, color: colorOf(label) };
    })
  );

  const rebet = () => {
    if (spinning || !lastBet) return;
    setBets({ ...lastBet });
    setHistory([]);
    setResult(null);
  };

  const wrapRef = useRef(null);
  const boardRef = useRef(null);
  const [boxScale, setBoxScale] = useState({ scale: 1, height: undefined });
  useEffect(() => {
    const measure = () => {
      if (!wrapRef.current || !boardRef.current) return;
      const w = wrapRef.current.clientWidth;
      const s = Math.min(1.4, w / BOARD_W);
      setBoxScale({ scale: s, height: boardRef.current.offsetHeight * s });
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (wrapRef.current) ro.observe(wrapRef.current);
    window.addEventListener("resize", measure);
    return () => { ro.disconnect(); window.removeEventListener("resize", measure); };
  }, []);

  if (!isAuthed) return (
    <div className="max-w-lg mx-auto px-4 py-24 text-center" data-testid="pro-roulette-signin-gate">
      <h2 className="font-display font-bold text-2xl">Sign in to play</h2>
      <Link to="/"><Button className="mt-6 font-semibold" data-testid="pro-roulette-go-home">Go Home</Button></Link>
    </div>
  );

  const total = Object.values(bets).reduce((a, b) => a + b, 0);

  const place = (id) => {
    if (spinning) return;
    if (coveredCount([...Object.keys(bets), id]) > PRO_MAX_COVERAGE) {
      toast({ title: "This type of betting is not allowed", description: `You can cover at most ${PRO_MAX_COVERAGE} numbers per spin on Pro Roulette.` });
      return;
    }
    setResult(null);
    setBets((p) => ({ ...p, [id]: (p[id] || 0) + chip }));
    setHistory((h) => [...h, id]);
  };
  const undo = () => {
    if (spinning || history.length === 0) return;
    const last = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    setBets((p) => {
      const amt = (p[last] || 0) - chip;
      const next = { ...p };
      if (amt > 0) next[last] = amt; else delete next[last];
      return next;
    });
  };
  const clearAll = () => { if (!spinning) { setBets({}); setHistory([]); } };

  const spin = async () => {
    if (total <= 0) { toast({ title: "Place a bet first" }); return; }
    if (total < 20) { toast({ title: "Minimum bet is ₹20", description: `Total stake ${inr(total)}` }); return; }
    if (total > 100000) { toast({ title: "Higher bet limit crossed", description: "Please bet ₹100000 or below." }); return; }
    if (total > balance) { toast({ title: "Insufficient balance", description: `Total stake ${inr(total)}` }); return; }
    const betList = Object.entries(bets).map(([id, amount]) => {
      const idx = id.indexOf(":");
      return { type: TYPE_MAP[id.slice(0, idx)], value: id.slice(idx + 1), amount };
    });
    setLastBet({ ...bets });
    unlockAudio();
    setSpinning(true); setResult(null);
    let res;
    try {
      res = await gamesApi.proRoulette({ bets: betList });
    } catch (e) {
      setSpinning(false);
      toast({ title: "Error", description: e?.response?.data?.detail || "Try again" });
      return;
    }
    const winIdx = res.result.index;
    playRouletteSpin(4600);
    setWheelRot((prev) => Math.ceil(prev / 360) * 360 + 360 * 4 + (360 - winIdx * SEG));
    setBallRot((prev) => prev - 360 * 7);
    setTimeout(async () => {
      await refresh();
      setResult(res.result);
      setRecent((prev) => [{ label: res.result.label, color: res.result.color }, ...prev].slice(0, 50));
      setBets({}); setHistory([]);
      streakRef.current = res.payout > 0 ? streakRef.current + 1 : 0;
      if (res.payout > 0) playCoinSplash();
      setReveal({ label: res.result.label, color: res.result.color, won: res.payout > 0, payout: res.payout, streak: streakRef.current });
      setSpinning(false);
    }, 4600);
  };

  const winLabel = result ? result.label : null;

  // full-number cell (straight)
  const NumBtn = ({ n, col, row }) => {
    const label = String(n);
    const c = colorOf(label);
    const amt = bets[`s:${label}`];
    const isWin = winLabel === label;
    return (
      <button data-testid={`pro-num-${n}`} disabled={spinning} onClick={() => place(`s:${label}`)}
        style={{ gridColumn: col, gridRow: row, zIndex: 5 }}
        className={`relative rounded text-white text-sm font-bold transition-[color,background-color,border-color,opacity,transform] border border-white/30
          ${c === "red" ? "bg-red-600" : "bg-zinc-900"}
          ${amt ? "ring-2 ring-yellow-400" : "hover:brightness-125"} ${isWin ? "outline outline-2 outline-yellow-300" : ""}`}>
        {n}
        {amt > 0 && <ChipBadge amt={amt} />}
      </button>
    );
  };

  // outside / dozen / column cell
  const OutBtn = ({ id, children, className = "", style, testid }) => {
    const amt = bets[id];
    const isWin = result && id === `col:${result.color}` && result.color !== "green";
    return (
      <button data-testid={testid} disabled={spinning} onClick={() => place(id)}
        style={{ ...style, zIndex: 5 }}
        className={`relative flex items-center justify-center font-bold text-xs rounded transition-[color,background-color,border-color,opacity,transform] border border-white/25
          ${amt ? "ring-2 ring-yellow-400" : "hover:brightness-125"} ${isWin ? "outline outline-2 outline-yellow-300" : ""} ${className}`}>
        {children}
        {amt > 0 && <ChipBadge amt={amt} />}
      </button>
    );
  };

  // inside-bet hitbox on borders / intersections
  const InsideBet = ({ id, gc, gr, shape }) => {
    const amt = bets[id];
    const nums = id.slice(id.indexOf(":") + 1).split("-");
    const isWin = winLabel && nums.includes(winLabel);
    return (
      <button data-testid={`pro-${id.replace(":", "-")}`} disabled={spinning} onClick={() => place(id)}
        style={{ gridColumn: gc, gridRow: gr, zIndex: amt ? 30 : 20 }}
        className="relative flex items-center justify-center group bg-transparent">
        <span className={`absolute rounded-full transition-colors
          ${shape === "corner" ? "h-4 w-4 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" : "inset-[1px]"}
          ${amt ? "bg-yellow-400/85" : `group-hover:bg-yellow-400/40 ${isWin ? "bg-yellow-300/60" : ""}`}`} />
        {amt > 0 && <ChipBadge amt={amt} />}
      </button>
    );
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 pb-28" data-testid="pro-roulette-page">
      <WinReveal open={!!reveal} {...(reveal || {})} onClose={() => setReveal(null)} />
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-display font-bold text-3xl">Pro Roulette</h1>
          <p className="text-sm text-muted-foreground">Single-Zero Roulette · straight, split, street & corner bets</p>
        </div>
        <span className="rounded-full bg-secondary px-3 py-1.5 text-sm font-semibold shrink-0" data-testid="pro-roulette-balance">
          <Coins className="h-4 w-4 inline mr-1 text-primary" />{inr(balance)}
        </span>
      </div>

      {/* Wheel */}
      <div className="relative rounded-2xl border border-border bg-gradient-to-b from-secondary/30 to-card p-6 flex flex-col items-center">
        <LastResults items={recent.slice(0, 10)} testId="pro-roulette-last-results" />
        <div className="relative" style={{ width: 300, height: 300 }}>
          <div className="absolute left-1/2 -translate-x-1/2 -top-1 z-20"
            style={{ width: 0, height: 0, borderLeft: "9px solid transparent", borderRight: "9px solid transparent", borderTop: "16px solid #facc15" }} />
          <LuxeWheel viewBox="0 0 300 300" width={300} height={300} cx={CX} cy={CY} r={R}
            pockets={WHEEL.map((label) => ({ label, color: colorOf(label) }))} labelFontSize={8}
            testId="pro-roulette-wheel"
            style={{
              transform: `rotate(${wheelRot}deg)`,
              transition: spinning ? "transform 4.4s cubic-bezier(0.12, 0.62, 0.2, 1)" : "none",
              filter: "drop-shadow(0 0 18px rgba(212,175,55,0.45))",
            }} />
          <WheelSheen />
          <div className="absolute inset-0" data-testid="pro-roulette-ball"
            style={{ transform: `rotate(${ballRot}deg)`, transition: spinning ? "transform 4.4s cubic-bezier(0.1, 0.6, 0.2, 1)" : "none" }}>
            <div className="absolute left-1/2 -translate-x-1/2 z-20" style={{ top: `${150 - 132}px`, width: 14, height: 14 }}>
              <PearlBall size={16} />
            </div>
          </div>
        </div>
        <div className="mt-4 h-7 text-center" data-testid="pro-roulette-result">
          {result ? (
            <span className="font-semibold">
              Ball landed on <span className="font-display font-bold text-lg" style={{ color: FILL[result.color] }}>{result.label}</span>
            </span>
          ) : (
            <span className="text-sm text-muted-foreground">{spinning ? "Ball is rolling…" : "Place your chips on the table"}</span>
          )}
        </div>
        <div className="mt-4 flex items-center justify-center gap-4">
          <div className="text-sm"><span className="text-muted-foreground">Stake </span><span className="font-display font-bold text-lg" data-testid="pro-total-stake">{inr(total)}</span><span className="ml-1 text-xs text-muted-foreground">(min ₹20 · max ₹100,000)</span></div>
          <Button className="font-semibold h-12 px-10 text-base" onClick={spin} disabled={spinning} data-testid="pro-roulette-spin-btn">
            <RotateCw className={`h-4 w-4 mr-1.5 ${spinning ? "animate-spin" : ""}`} /> {spinning ? "Spinning…" : "Spin"}
          </Button>
        </div>
      </div>

      {/* Board (auto-scales to fit width) */}
      <div className="mt-4 rounded-2xl border border-yellow-700/50 p-3 sm:p-4 shadow-inner" data-testid="pro-roulette-table"
        style={{ background: "linear-gradient(160deg,#0b6b3a 0%,#08592f 55%,#064a28 100%)" }}>
        <div ref={wrapRef} className="w-full overflow-hidden mx-auto" style={{ height: boxScale.height }}>
          <div ref={boardRef} className="space-y-1" style={{ width: BOARD_W, transform: `scale(${boxScale.scale})`, transformOrigin: "top left" }}>
            {/* numbers + zero + inside bets + column bets */}
            <div className="grid" style={{ gridTemplateColumns: FINE_COLS, gridTemplateRows: FINE_ROWS }}>
              {/* zero */}
              <NumBtnZero amt={bets["s:0"]} isWin={winLabel === "0"} spinning={spinning} onPlace={() => place("s:0")} />
              {ROW_TOP.map((n, j) => <NumBtn key={n} n={n} col={2 * (j + 1)} row={2} />)}
              {ROW_MID.map((n, j) => <NumBtn key={n} n={n} col={2 * (j + 1)} row={4} />)}
              {ROW_BOT.map((n, j) => <NumBtn key={n} n={n} col={2 * (j + 1)} row={6} />)}
              {/* 2:1 column bets */}
              <OutBtn id="c:3" testid="pro-col-3" style={{ gridColumn: 25, gridRow: 2 }} className="bg-transparent text-white text-[11px]">2:1</OutBtn>
              <OutBtn id="c:2" testid="pro-col-2" style={{ gridColumn: 25, gridRow: 4 }} className="bg-transparent text-white text-[11px]">2:1</OutBtn>
              <OutBtn id="c:1" testid="pro-col-1" style={{ gridColumn: 25, gridRow: 6 }} className="bg-transparent text-white text-[11px]">2:1</OutBtn>
              {/* inside-bet hitboxes */}
              {INSIDE.map((b) => <InsideBet key={b.id} {...b} />)}
            </div>

            {/* single line under all the numbers */}
            <div style={{ height: 3, borderRadius: 3, background: "rgba(250,204,21,0.6)" }} />

            {/* dozens */}
            <div className="grid" style={{ gridTemplateColumns: FINE_COLS, gridAutoRows: "34px" }}>
              <OutBtn id="d:1-12" testid="pro-dozen-1" style={{ gridColumn: "2 / 9" }} className="bg-transparent text-white mx-0.5">1st 12</OutBtn>
              <OutBtn id="d:13-24" testid="pro-dozen-2" style={{ gridColumn: "10 / 17" }} className="bg-transparent text-white mx-0.5">2nd 12</OutBtn>
              <OutBtn id="d:25-36" testid="pro-dozen-3" style={{ gridColumn: "18 / 25" }} className="bg-transparent text-white mx-0.5">3rd 12</OutBtn>
            </div>

            {/* even-money outside bets */}
            <div className="grid" style={{ gridTemplateColumns: FINE_COLS, gridAutoRows: "34px" }}>
              <OutBtn id="h:1-18" testid="pro-half-low" style={{ gridColumn: "2 / 5" }} className="bg-transparent text-white mx-0.5">1-18</OutBtn>
              <OutBtn id="p:even" testid="pro-even" style={{ gridColumn: "6 / 9" }} className="bg-transparent text-white mx-0.5">EVEN</OutBtn>
              <OutBtn id="col:red" testid="pro-red" style={{ gridColumn: "10 / 13" }} className="bg-red-600 text-white mx-0.5">RED</OutBtn>
              <OutBtn id="col:black" testid="pro-black" style={{ gridColumn: "14 / 17" }} className="bg-zinc-900 text-white border border-white/10 mx-0.5">BLACK</OutBtn>
              <OutBtn id="p:odd" testid="pro-odd" style={{ gridColumn: "18 / 21" }} className="bg-transparent text-white mx-0.5">ODD</OutBtn>
              <OutBtn id="h:19-36" testid="pro-half-high" style={{ gridColumn: "22 / 25" }} className="bg-transparent text-white mx-0.5">19-36</OutBtn>
            </div>
          </div>
        </div>

        {/* footer */}
        <p className="mt-4 text-xs text-muted-foreground text-center">
          Tap a number for a straight bet (35:1) · tap the line between two numbers for a Split (17:1) · the edge above a column for a Street (11:1) · a corner where 4 numbers meet (8:1). Dozens/Columns 2:1 · outside 1:1.
        </p>
      </div>

      {lastBet && Object.keys(lastBet).length > 0 && (
        <div className="mt-4 flex justify-center">
          <button onClick={rebet} disabled={spinning} data-testid="pro-roulette-rebet-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 text-primary px-4 py-1.5 text-xs font-semibold hover:bg-primary/25 disabled:opacity-40 transition-colors">
            <RotateCw className="h-3.5 w-3.5" /> Rebet ₹{Object.values(lastBet).reduce((a, b) => a + b, 0)}
          </button>
        </div>
      )}

      {/* Undo / Clear (left side, below board, before chips) */}
      <div className="mt-4 flex items-center gap-4">
        <button onClick={undo} disabled={spinning || history.length === 0} data-testid="pro-undo"
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-40"><Undo2 className="h-3.5 w-3.5" /> Undo</button>
        <button onClick={clearAll} disabled={spinning || total === 0} data-testid="pro-clear"
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-red-400 disabled:opacity-40"><X className="h-3.5 w-3.5" /> Clear</button>
      </div>

      {/* Chip selector (below board) */}
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mr-1">Chip</span>
        {[20, 50, 100, 500, 1000, 2000, 3000, 5000, 10000].map((q) => {
          const special = q === 10000;
          const active = chip === q;
          return (
            <button key={q} onClick={() => setChip(q)} data-testid={`pro-chip-${q}`}
              className={`rounded-full font-bold border-2 transition-[color,background-color,border-color,opacity,transform] ${special ? "h-10 min-w-[64px] px-3.5 text-sm" : "h-9 min-w-[52px] px-3 text-sm"} ${
                active
                  ? "bg-yellow-400 text-black border-yellow-300 scale-105"
                  : special
                    ? "text-black border-yellow-200/70 shadow-md hover:brightness-110"
                    : "bg-secondary text-foreground border-transparent hover:brightness-125"
              }`}
              style={special && !active ? { background: "linear-gradient(135deg,#fde68a,#f59e0b 55%,#b45309)" } : undefined}>
              {q}
            </button>
          );
        })}
      </div>
      <TodaysWinners />
    </div>
  );
}

function NumBtnZero({ amt, isWin, spinning, onPlace }) {
  return (
    <button data-testid="pro-num-0" disabled={spinning} onClick={onPlace}
      style={{ gridColumn: 1, gridRow: "2 / 7", zIndex: 5 }}
      className={`relative bg-green-600 text-white rounded-l-lg text-base font-bold transition-[color,background-color,border-color,opacity,transform] border border-white/30
        ${amt ? "ring-2 ring-yellow-400" : "hover:brightness-125"} ${isWin ? "outline outline-2 outline-yellow-300" : ""}`}>
      0
      {amt > 0 && <ChipBadge amt={amt} />}
    </button>
  );
}

function ChipBadge({ amt }) {
  const n = Number(amt);
  const special = n >= 10000;
  const size = special ? 36 : n >= 5000 ? 30 : 26;
  const label = n >= 1000 ? (Number.isInteger(n / 1000) ? n / 1000 : (n / 1000).toFixed(1)) + "K" : String(n);
  return (
    <span
      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-30 grid place-items-center rounded-full font-extrabold text-black pointer-events-none"
      style={{
        height: size, width: size, fontSize: special ? 11 : 9,
        background: special
          ? "radial-gradient(circle at 50% 30%, #fff7cc, #f5b90b 55%, #b45309)"
          : "radial-gradient(circle at 50% 32%, #fde68a, #f59e0b)",
        border: special ? "2px solid #fff7cc" : "2px dashed rgba(0,0,0,0.6)",
        boxShadow: special
          ? "0 0 12px rgba(245,158,11,0.95), 0 2px 5px rgba(0,0,0,0.6)"
          : "0 2px 5px rgba(0,0,0,0.55)",
      }}
    >
      {label}
    </span>
  );
}
